import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { DepartmentBadge } from './ProgressParts';
import { apiFetch, v1Path } from '../../lib/api';
import { isWorkspaceProject } from '../../lib/workspace-projects';
import type { ProjectDto } from '../../lib/work-types';

export type PlannedTask = { title: string; project_id: string; due_date: string | null };

let streamsCache: Promise<ProjectDto[]> | null = null;

/** Live streams a planned task can go to. */
export function useDepartments(): ProjectDto[] {
  const [streams, setStreams] = useState<ProjectDto[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!streamsCache) {
      streamsCache = apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0'))
        .then((res) => res.items.filter((project) => !isWorkspaceProject(project) && !project.archived_at))
        .catch((err: unknown) => {
          streamsCache = null;
          throw err;
        });
    }
    streamsCache.then((list) => !cancelled && setStreams(list)).catch(() => !cancelled && setStreams([]));
    return () => {
      cancelled = true;
    };
  }, []);
  return streams;
}

export type StreamPlan = { project_id: string; tasks: Array<{ title: string; due_date: string | null }> };

export function toPlannedTasks(plans: StreamPlan[]): PlannedTask[] {
  return plans.flatMap((plan) =>
    plan.tasks.map((task) => ({ title: task.title.trim(), project_id: plan.project_id, due_date: task.due_date })),
  );
}

/** Every stream has at least one task, and every task has a title. */
export function streamPlansComplete(plans: StreamPlan[]): boolean {
  return plans.every((plan) => plan.tasks.length > 0 && plan.tasks.every((task) => task.title.trim() !== ''));
}

/** The streams an initiative needs, each with the tasks that stream will do. */
export function StreamTasksEditor({
  value,
  onChange,
}: {
  value: StreamPlan[];
  onChange: (value: StreamPlan[]) => void;
}) {
  const streams = useDepartments();
  const byId = useMemo(() => new Map(streams.map((stream) => [stream.id, stream])), [streams]);
  const available = streams.filter((stream) => !value.some((plan) => plan.project_id === stream.id));

  const setPlan = (index: number, plan: StreamPlan) => onChange(value.map((p, i) => (i === index ? plan : p)));

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          Which streams are involved? Add each one, then the tasks it needs to do.
        </p>
      ) : null}
      {value.map((plan, index) => {
        const stream = byId.get(plan.project_id);
        const name = stream?.name ?? 'Stream';
        return (
          <div key={plan.project_id} className="rounded-lg border" data-testid="stream-plan">
            <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
              <DepartmentBadge projectId={plan.project_id} name={name} size="md" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {plan.tasks.length} {plan.tasks.length === 1 ? 'task' : 'tasks'}
              </span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                aria-label={`Remove ${name}`}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
            <div className="space-y-2 p-3">
              {plan.tasks.length === 0 ? (
                <p className="text-xs text-amber-700 dark:text-amber-300">Add at least one task for {name}.</p>
              ) : null}
              {plan.tasks.map((task, taskIndex) => (
                <div key={taskIndex} className="grid grid-cols-[minmax(0,1fr)_9.5rem_auto] items-center gap-2">
                  <Input
                    aria-label={`${name} task ${taskIndex + 1}`}
                    placeholder="What needs to happen"
                    value={task.title}
                    onChange={(event) =>
                      setPlan(index, {
                        ...plan,
                        tasks: plan.tasks.map((t, i) => (i === taskIndex ? { ...t, title: event.target.value } : t)),
                      })
                    }
                  />
                  <Input
                    type="date"
                    aria-label={`${name} task ${taskIndex + 1} go-live date`}
                    title="Go-live date (optional)"
                    value={task.due_date ?? ''}
                    onChange={(event) =>
                      setPlan(index, {
                        ...plan,
                        tasks: plan.tasks.map((t, i) => (i === taskIndex ? { ...t, due_date: event.target.value || null } : t)),
                      })
                    }
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    aria-label={`Remove ${name} task ${taskIndex + 1}`}
                    onClick={() => setPlan(index, { ...plan, tasks: plan.tasks.filter((_, i) => i !== taskIndex) })}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 text-muted-foreground"
                onClick={() => setPlan(index, { ...plan, tasks: [...plan.tasks, { title: '', due_date: null }] })}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Add task to {name}
              </Button>
            </div>
          </div>
        );
      })}
      {available.length === 0 ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5">
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add stream
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
            {available.map((stream) => (
              <DropdownMenuItem
                key={stream.id}
                onSelect={() => onChange([...value, { project_id: stream.id, tasks: [{ title: '', due_date: null }] }])}
              >
                <DepartmentBadge projectId={stream.id} name={stream.name} />
                <span className="ml-2">{stream.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export function createStreamTasks(initiativeId: string, plans: StreamPlan[]): Promise<{ keys: string[] }> {
  return apiFetch<{ keys: string[] }>(v1Path(`/initiatives/${initiativeId}/tasks`), {
    method: 'POST',
    body: JSON.stringify({ tasks: toPlannedTasks(plans) }),
  });
}
